import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useProducts, useCategories, useCMSSiteSettings } from "@/hooks/use-cms-api";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FeaturedBrands } from "@/components/home/FeaturedBrands";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useSiteUrl } from "@/hooks/use-site-url";
import { useCurrency } from "@/contexts/MarketContext";
import { useCart } from "@/contexts/CartContext";
import { 
  ArrowRight, 
  Pill, 
  Shield,
  Clock,
  CheckCircle2,
  Star,
  Users,
  Leaf,
  Package,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Sparkles
} from "lucide-react";

// Variant D: Editorial/Trust - Supplements Focus
// Focus: Building authority and trust through premium supplements

export const VariantDEditorial = () => {
  const { siteUrl, siteName } = useSiteUrl();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const categoriesScrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const { data: productsData, isLoading: productsLoading } = useProducts({ limit: 50 });
  
  const { data: siteSettings } = useCMSSiteSettings();

  // Use cached hero image URL for immediate preload, update cache when CMS data arrives
  const cachedHeroImage = typeof window !== 'undefined' ? localStorage.getItem('hero_image_url') : null;
  const heroImage = siteSettings?.hero_image_url || cachedHeroImage;
  
  useEffect(() => {
    if (siteSettings?.hero_image_url) {
      localStorage.setItem('hero_image_url', siteSettings.hero_image_url);
    }
  }, [siteSettings?.hero_image_url]);

  // Derive categories from actual supplement products to ensure only valid supplement categories are shown
  const products = productsData?.products || [];
  
  // Count products per category
  const productCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    products.forEach(product => {
      if (product.category) {
        counts[product.category] = (counts[product.category] || 0) + 1;
      }
    });
    return counts;
  }, [products]);
  
  const categories = useMemo(() => {
    // Extract unique category names from supplement products
    const uniqueCategoryNames = new Set<string>();
    products.forEach(product => {
      if (product.category) {
        uniqueCategoryNames.add(product.category);
      }
    });
    
    // Create categories from products, enriching with CMS metadata if available
    const derivedCategories = Array.from(uniqueCategoryNames).map(categoryName => {
      // Try to find matching category in CMS for rich metadata (images, descriptions)
      const cmsCategory = (categoriesData || []).find(
        c => c.is_active && c.name === categoryName
      );
      
      if (cmsCategory) {
        return cmsCategory;
      }
      
      // Fallback: Create a basic category from the product's category name
      return {
        id: categoryName.toLowerCase().replace(/\s+/g, '-'),
        name: categoryName,
        slug: categoryName.toLowerCase().replace(/\s+/g, '-'),
        description: null,
        image_url: null,
        is_active: true,
        display_order: 0,
        created_at: '',
        updated_at: '',
      };
    });
    
    return derivedCategories.sort((a, b) => a.name.localeCompare(b.name));
  }, [products, categoriesData]);
  
  const loading = categoriesLoading || productsLoading;

  const handleCategoriesScroll = (direction: 'left' | 'right') => {
    if (!categoriesScrollRef.current) return;
    const scrollAmount = 400;
    const newScrollLeft = direction === 'left' 
      ? categoriesScrollRef.current.scrollLeft - scrollAmount
      : categoriesScrollRef.current.scrollLeft + scrollAmount;
    
    categoriesScrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  };

  const updateArrowVisibility = useCallback(() => {
    if (!categoriesScrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = categoriesScrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    const scrollContainer = categoriesScrollRef.current;
    if (scrollContainer) {
      // Use requestAnimationFrame to batch DOM reads and prevent forced reflows
      let rafId: number | null = null;
      const throttledUpdate = () => {
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          updateArrowVisibility();
          rafId = null;
        });
      };
      
      scrollContainer.addEventListener('scroll', throttledUpdate, { passive: true });
      updateArrowVisibility();
      return () => {
        scrollContainer.removeEventListener('scroll', throttledUpdate);
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }
  }, [categories, updateArrowVisibility]);

  const handleAddToCart = (productId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(productId, true);
  };

  const pageTitle = `Premium Health Supplements - ${siteName}`;
  const pageDescription = "Science-backed supplements for optimal health. Premium quality ingredients, third-party tested for purity and potency.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {heroImage && (
          <link rel="preload" as="image" href={heroImage} />
        )}
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1">
          {/* Editorial Hero */}
          <section className="relative min-h-[70vh] flex items-center bg-gradient-to-br from-background via-primary/5 to-background overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
            <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />
            
            <div className="container mx-auto px-4 py-20 relative z-10">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <div className="space-y-4">
                    <Badge variant="secondary" className="text-sm px-4 py-2">
                      <Pill className="h-4 w-4 mr-2" />
                      Premium Formulas • Third-Party Tested • GMP Certified
                    </Badge>
                    <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                      <span className="text-muted-foreground">Elevate Your Health With</span>
                      <br />
                      <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
                        Premium
                      </span>
                      <br />
                      <span className="text-foreground">Supplements</span>
                    </h1>
                    <p className="text-xl text-muted-foreground leading-relaxed">
                      Science-backed formulas with premium ingredients. Experience the difference that quality makes in your daily wellness routine.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" className="text-lg px-8 py-6 shadow-lg" asChild>
                      <Link to="/products">
                        Shop Supplements
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                    <Button size="lg" variant="outline" className="text-lg px-8 py-6" asChild>
                      <a href="#how-it-works">Learn More</a>
                    </Button>
                  </div>
                </div>

                {heroImage && (
                  <div className="hidden lg:block">
                    <div className="relative group">
                      <img 
                        src={heroImage} 
                        srcSet={`${heroImage} 512w, ${heroImage} 1024w`}
                        sizes="(max-width: 1024px) 100vw, 512px"
                        alt="Premium health supplements and wellness products" 
                        className="w-full max-w-lg rounded-2xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]"
                        fetchPriority="high"
                        loading="eager"
                        width={512}
                        height={512}
                        decoding="async"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Trusted Supplements Stats */}
          <section className="py-12 border-y bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-8">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Quality You Can Trust
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Pill className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">50+</div>
                  <div className="text-sm text-muted-foreground">Supplements</div>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Leaf className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">100%</div>
                  <div className="text-sm text-muted-foreground">Pure Ingredients</div>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">GMP</div>
                  <div className="text-sm text-muted-foreground">Certified Facility</div>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">2-3</div>
                  <div className="text-sm text-muted-foreground">Day Shipping</div>
                </div>
              </div>
            </div>
          </section>

          {/* Supplement Categories */}
          <section id="categories" className="py-20 bg-gradient-to-b from-primary/5 to-background">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <Badge variant="secondary" className="mb-4">
                  <Sparkles className="h-3 w-3 mr-2" />
                  Premium Formulas
                </Badge>
                <h2 className="text-4xl font-bold mb-6">
                  Support Every Aspect of Your Health
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  From energy and focus to immunity and recovery, our supplements are formulated for optimal results
                </p>
              </div>

              <div className="relative">
                {showLeftArrow && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-lg"
                    onClick={() => handleCategoriesScroll('left')}
                    aria-label="Scroll categories left"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}
                {showRightArrow && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-lg"
                    onClick={() => handleCategoriesScroll('right')}
                    aria-label="Scroll categories right"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}

                <div ref={categoriesScrollRef} className="overflow-x-auto scrollbar-hide">
                  <div className="flex gap-6 pb-4">
                    {categories.map((category) => (
                      <Link
                        key={category.slug}
                        to={`/products?categories=${encodeURIComponent(category.name)}`}
                        className="flex-none w-80"
                      >
                        <Card className="h-full hover:shadow-xl hover:border-primary/50 transition-all group">
                          <CardContent className="p-0">
                            <div className="aspect-video bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 relative overflow-hidden flex items-center justify-center">
                              {category.image_url ? (
                                <img src={category.image_url} alt={category.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                              ) : (
                                <div className="flex flex-col items-center justify-center gap-3">
                                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                    <Pill className="h-10 w-10 text-primary/60 group-hover:text-primary transition-colors" />
                                  </div>
                                  <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
                                    {category.name}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="p-6 space-y-3">
                              <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold group-hover:text-primary transition-colors">
                                  {category.name}
                                </h3>
                                <Badge variant="secondary" className="text-xs">
                                  {productCountByCategory[category.name] || 0} {productCountByCategory[category.name] === 1 ? 'product' : 'products'}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {category.description || "Premium supplements for optimal health and wellness"}
                              </p>
                              <Button variant="ghost" size="sm" className="group-hover:text-primary">
                                Shop Now
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Brands */}
          <FeaturedBrands />

          {/* How It Works */}
          <section id="how-it-works" className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">Your Wellness Journey Starts Here</h2>
                <p className="text-lg text-muted-foreground">
                  Simple steps to better health with premium supplements
                </p>
              </div>

              <div className="max-w-5xl mx-auto space-y-8">
                {[
                  {
                    step: "01",
                    title: "Choose Your Supplements",
                    description: "Browse our curated selection of premium supplements or use our AI assistant to get personalized recommendations based on your health goals.",
                    icon: Sparkles,
                  },
                  {
                    step: "02",
                    title: "Fast & Free Shipping",
                    description: "Your order ships within 24 hours with free delivery on orders over $50. Each supplement comes with detailed usage instructions.",
                    icon: Package,
                  },
                  {
                    step: "03",
                    title: "Premium Quality Guaranteed",
                    description: "All our supplements are manufactured in GMP-certified facilities, third-party tested for purity, and backed by science-based formulations.",
                    icon: Shield,
                  },
                  {
                    step: "04",
                    title: "Track Your Progress",
                    description: "Experience the benefits of consistent supplementation. Our team is here to support your wellness journey with expert guidance when you need it.",
                    icon: TrendingUp,
                  },
                ].map((item, index) => (
                  <div key={index} className="flex gap-6 items-start">
                    <div className="flex-none">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <item.icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1 pt-2">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-bold text-primary">{item.step}</span>
                        <h3 className="text-2xl font-bold">{item.title}</h3>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">Common Questions</h2>
                <p className="text-lg text-muted-foreground">
                  Everything you need to know about our supplements
                </p>
              </div>

              <Accordion type="single" collapsible className="max-w-3xl mx-auto">
                <AccordionItem value="quality">
                  <AccordionTrigger className="text-left">
                    How do you ensure supplement quality?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    All our supplements are manufactured in GMP-certified facilities and undergo rigorous third-party testing for purity, potency, and safety. We use only premium, bioavailable ingredients with no artificial fillers or additives.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="gmp-certification">
                  <AccordionTrigger className="text-left">
                    What does GMP certification mean?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    GMP (Good Manufacturing Practices) is a system that ensures products are consistently produced and controlled according to quality standards. Our GMP-certified facilities follow strict protocols for cleanliness, equipment maintenance, and quality control, guaranteeing you receive safe and effective supplements.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="hsa-fsa">
                  <AccordionTrigger className="text-left">
                    Are supplements HSA/FSA eligible?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Many of our supplements may be HSA/FSA eligible depending on your plan and if you have a Letter of Medical Necessity. We provide detailed receipts for your records.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="shipping">
                  <AccordionTrigger className="text-left">
                    How fast is shipping?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Orders are processed and shipped within 24 hours. Standard delivery takes 2-3 business days, and we offer free shipping on orders over $50.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="returns">
                  <AccordionTrigger className="text-left">
                    What is your return policy?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    We offer a 30-day satisfaction guarantee. If you're not completely satisfied with your purchase, contact our support team for a full refund or exchange.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-primary/80 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary-foreground)/0.1),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,hsl(var(--primary-foreground)/0.08),transparent_50%)]" />
            
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
                Ready to Elevate Your Wellness?
              </h2>
              <p className="text-lg md:text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
                Discover our best-selling supplements trusted by thousands of customers
              </p>
              <Button 
                size="lg" 
                variant="secondary"
                className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
                asChild
              >
                <Link to="/products?sort=popularity">
                  Shop Best Sellers
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};
